import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SENS_EVOLUTION_LABELS,
  TYPE_EVENEMENT_LABELS,
  VALIDATION_TYPE_LABELS,
  adresseEcart,
  type DiscrepancyRow,
  type EntreeFil,
} from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { classeMontant } from '../../components/montant';

/** Natures affichables, dans l'ordre des filtres. */
const NATURES = [
  { cle: 'TOUT', libelle: 'Tout' },
  { cle: 'EVENEMENT', libelle: 'Évènements' },
  { cle: 'COMMENTAIRE', libelle: 'Commentaires' },
  { cle: 'VALIDATION', libelle: 'Validations' },
] as const;

type FiltreNature = (typeof NATURES)[number]['cle'];

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function formaterDelta(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return `${nombre > 0 ? '+' : ''}${formaterNombre(valeur)}`;
}

function formaterHorodatage(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formaterJour(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return jour && mois && annee ? `${jour}/${mois}` : iso;
}

/** Le sens d'une évolution, déduit des deux valeurs qu'elle porte. */
function sensDeLEntree(entree: Extract<EntreeFil, { nature: 'EVENEMENT' }>): string | null {
  if (entree.type !== 'EVOLUTION' || entree.valeurAvant === null) return null;
  const avant = Math.abs(Number(entree.valeurAvant));
  const apres = Math.abs(Number(entree.valeurApres));
  const signeChange =
    Number(entree.valeurAvant) !== 0 &&
    Number(entree.valeurApres) !== 0 &&
    Number(entree.valeurAvant) < 0 !== Number(entree.valeurApres) < 0;

  if (signeChange) return SENS_EVOLUTION_LABELS.CHANGEMENT_DE_SENS;
  return apres < avant ? SENS_EVOLUTION_LABELS.AMELIORATION : SENS_EVOLUTION_LABELS.AGGRAVATION;
}

function classeEvenement(entree: Extract<EntreeFil, { nature: 'EVENEMENT' }>): string {
  if (entree.type === 'SOLDE') return 'fil__puce fil__puce--solde';
  if (entree.type === 'APPARITION') return 'fil__puce fil__puce--apparition';
  const sens = sensDeLEntree(entree);
  return sens === SENS_EVOLUTION_LABELS.AMELIORATION
    ? 'fil__puce fil__puce--amelioration'
    : 'fil__puce fil__puce--aggravation';
}

function Evenement({
  entree,
  onJour,
}: {
  entree: Extract<EntreeFil, { nature: 'EVENEMENT' }>;
  onJour: (jour: string) => void;
}): ReactElement {
  const sens = sensDeLEntree(entree);

  return (
    <>
      <p className="fil__titre">
        <span className={classeEvenement(entree)} aria-hidden="true" />
        <strong>{TYPE_EVENEMENT_LABELS[entree.type]}</strong>
        {entree.type === 'EVOLUTION' && entree.valeurAvant !== null && (
          <>
            {' '}
            {formaterNombre(entree.valeurAvant)} → {formaterNombre(entree.valeurApres)} UVC{' '}
            <span className={classeMontant(entree.delta)}>({formaterDelta(entree.delta)})</span>
            {sens && <span className="etiquette">{sens}</span>}
          </>
        )}
        {entree.type === 'APPARITION' && <> à {formaterNombre(entree.valeurApres)} UVC</>}
        {entree.type === 'SOLDE' && <> — retour à zéro</>}
      </p>
      <p className="fil__meta">
        {formaterHorodatage(entree.horodatage)}
        {entree.importFilename ? ` · import « ${entree.importFilename} »` : ' · recalcul'}
      </p>
      {entree.journees.length > 0 && (
        <p className="fil__journees">
          Journées modifiées :{' '}
          {entree.journees.map((jour) => (
            <button
              key={jour}
              type="button"
              className="bouton bouton--lien"
              onClick={() => onJour(jour)}
              title={`Voir les mouvements du ${jour} dans « Jour par jour »`}
            >
              {formaterJour(jour)}
            </button>
          ))}
        </p>
      )}
    </>
  );
}

/**
 * Fil de vie d'un écart — règle 14 v11.2.
 *
 * Un seul ruban pour ce que la machine a constaté et ce que les humains ont
 * fait. Les séparer obligerait à lire deux colonnes en parallèle pour répondre à
 * la seule question qui compte : que s'est-il passé sur cet écart, et dans quel
 * ordre.
 *
 * Ce que le fil ne fait **pas** : désigner le mouvement qui a corrigé l'écart.
 * Sur des cumuls, l'attribution serait arbitraire. Il donne la date, l'import et
 * les journées touchées — l'humain juge.
 */
export function FilDeVie({
  ecart,
  peutCommenter,
  onJour,
}: {
  ecart: DiscrepancyRow;
  peutCommenter: boolean;
  /** Ouvre une journée dans l'onglet « Jour par jour ». */
  onJour: (jour: string) => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const adresse = adresseEcart(ecart.maille, ecart.cle);
  const [filtre, setFiltre] = useState<FiltreNature>('TOUT');
  const [typeId, setTypeId] = useState('');
  const [texte, setTexte] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const journal = useQuery({
    queryKey: ['ecart', ecart.id, 'journal'],
    queryFn: () => api.journalEcart(ecart.id),
  });

  const types = useQuery({
    queryKey: ['comment-types', 'actifs'],
    queryFn: () => api.commentTypes(true),
    staleTime: 5 * 60 * 1000,
  });

  const ajout = useMutation({
    mutationFn: (body: { typeId: string; texte: string }) =>
      api.ajouterCommentaire(adresse, body),
    onSuccess: () => {
      setTexte('');
      setErreur(null);
      void queryClient.invalidateQueries({ queryKey: ['ecart', ecart.id, 'journal'] });
      // La liste montre le type du dernier commentaire : elle doit suivre.
      void queryClient.invalidateQueries({ queryKey: ['discrepancies'] });
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const entrees = useMemo(() => {
    const toutes = journal.data?.entrees ?? [];
    return filtre === 'TOUT' ? toutes : toutes.filter((e) => e.nature === filtre);
  }, [journal.data, filtre]);

  function soumettre(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!typeId) {
      setErreur('Choisissez un type de commentaire.');
      return;
    }
    if (texte.trim() === '') {
      setErreur('Le commentaire ne peut pas être vide.');
      return;
    }
    ajout.mutate({ typeId, texte: texte.trim() });
  }

  return (
    <section className="carte">
      <div className="barre-actions barre-actions--titre">
        <h2>Fil de vie</h2>
        <div className="onglets onglets--secondaires" role="tablist" aria-label="Filtrer le fil">
          {NATURES.map((nature) => (
            <button
              key={nature.cle}
              type="button"
              role="tab"
              aria-selected={filtre === nature.cle}
              className={`onglet${filtre === nature.cle ? ' onglet--actif' : ''}`}
              onClick={() => setFiltre(nature.cle)}
            >
              {nature.libelle}
            </button>
          ))}
        </div>
      </div>

      {peutCommenter && (
        <form className="fil__saisie" onSubmit={soumettre}>
          <label className="champ" htmlFor="commentaire-type">
            <span>Type</span>
            <select
              id="commentaire-type"
              value={typeId}
              onChange={(event) => setTypeId(event.target.value)}
            >
              <option value="">Choisir…</option>
              {(types.data ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.libelle}
                </option>
              ))}
            </select>
          </label>
          <label className="champ champ--large" htmlFor="commentaire-texte">
            <span>Commentaire</span>
            <input
              id="commentaire-texte"
              type="text"
              maxLength={2000}
              placeholder="Ce que vous avez constaté, fait ou demandé"
              value={texte}
              onChange={(event) => setTexte(event.target.value)}
            />
          </label>
          <button type="submit" className="bouton" disabled={ajout.isPending}>
            {ajout.isPending ? 'Enregistrement…' : 'Commenter'}
          </button>
        </form>
      )}

      <p className="sous-titre">
        Un commentaire <strong>n&apos;a aucun effet</strong> sur l&apos;état de l&apos;écart, les
        vues ou les indicateurs : seuls une validation ou un solde constaté sortent un écart de la
        vue quotidienne. Un commentaire ne se modifie pas — pour corriger, écrivez-en un nouveau.
      </p>

      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}

      {journal.isPending && <p className="chargement">Chargement du fil…</p>}
      {journal.isError && (
        <p className="alerte alerte--erreur" role="alert">
          {messageErreur(journal.error)}
        </p>
      )}

      {journal.data?.anterieurAuJournal && (
        <p className="alerte alerte--info" role="note">
          Cet écart est né avant la mise en place du suivi des évolutions
          {journal.data.journalDemarreLe
            ? ` (actif depuis le ${formaterJour(journal.data.journalDemarreLe)}/${journal.data.journalDemarreLe.slice(0, 4)})`
            : ''}
          . Son historique démarre ici : rien n&apos;a été reconstitué.
        </p>
      )}

      {journal.data && entrees.length === 0 && (
        <p className="alerte alerte--info" role="status">
          {filtre === 'TOUT'
            ? 'Rien à afficher pour l’instant.'
            : 'Aucune entrée de cette nature dans le fil.'}
        </p>
      )}

      <ol className="fil">
        {entrees.map((entree) => (
          <li key={`${entree.nature}-${entree.id}`} className={`fil__entree fil__entree--${entree.nature.toLowerCase()}`}>
            {entree.nature === 'EVENEMENT' && <Evenement entree={entree} onJour={onJour} />}

            {entree.nature === 'COMMENTAIRE' && (
              <>
                <p className="fil__titre">
                  <span className="fil__puce fil__puce--commentaire" aria-hidden="true" />
                  <span className="etiquette">{entree.typeLibelle}</span> {entree.texte}
                </p>
                <p className="fil__meta">
                  {formaterHorodatage(entree.horodatage)}
                  {entree.auteur ? ` · ${entree.auteur}` : ''}
                </p>
              </>
            )}

            {entree.nature === 'VALIDATION' && (
              <>
                <p className="fil__titre">
                  <span className="fil__puce fil__puce--validation" aria-hidden="true" />
                  <strong>
                    Validation {VALIDATION_TYPE_LABELS[entree.typeValidation].toLowerCase()}
                  </strong>{' '}
                  {formaterNombre(entree.quantite)} UVC
                  {entree.annulee && <span className="etiquette etiquette--inactif">Annulée</span>}
                </p>
                <p className="fil__meta">
                  {formaterHorodatage(entree.horodatage)}
                  {entree.auteur ? ` · ${entree.auteur}` : ''}
                </p>
                <p className="fil__texte">{entree.commentaire}</p>
              </>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
